import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Container, Box, AppBar, Toolbar, Typography, Button, IconButton, Menu, MenuItem, useTheme, useMediaQuery } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { AccountCircle } from '@mui/icons-material';
import { useAuth } from './contexts/AuthContext';
import { CopyProvider } from './contexts/CopyContext';
import { UploadProvider } from './contexts/UploadContext';
import { PdfViewerProvider } from './contexts/PdfViewerContext';
import { Squeeze as Hamburger } from 'hamburger-react';
import { scaledDrawerWidth } from './theme';
import { getZoomCompensatedSx } from './utils/zoomCompensation';
import Logo from './components/Logo';
import ProtectedRoute from './components/ProtectedRoute';
import ProjectAccessGate from './components/ProjectAccessGate';
import { hasCapability, CAP } from './utils/permissions';
import { ROLE } from './utils/roles';
import NotificationsCenter from './components/NotificationsCenter';
import HamburgerMenu from './components/HamburgerMenu';
import SessionLoadingScreen from './components/SessionLoadingScreen';
import LoginPage from './pages/LoginPage';
import LoadingPage from './pages/LoadingPage';
import UserAvatar from './components/UserAvatar';
import DashboardPage from './pages/DashboardPage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectRevisionsPage from './pages/ProjectRevisionsPage';
import ProjectCreationPage from './pages/ProjectCreationPage';
import DocumentsPage from './pages/DocumentsPage';
import DocumentRevisionsPage from './pages/DocumentRevisionsPage';
import SandboxesPage from './pages/SandboxesPage';
import LCMsPage from './pages/LCMsPage';
import LCMLabPage from './pages/LCMLabPage';
import ResultsPage from './pages/ResultsPage';
import UsersPage from './pages/UsersPage';
import UserProfilePage from './pages/UserProfilePage';
import TimeOffPage from './pages/TimeOffPage';
import HRPage from './pages/HRPage';
import HRManagementPage from './pages/HRManagementPage';
import HRUserDetailsPage from './pages/HRUserDetailsPage';
import CVBankPage from './pages/CVBankPage';
import ExpensesPage from './pages/ExpensesPage';
import EmployeeDocumentsPage from './pages/EmployeeDocumentsPage';
import TimesheetPage from './pages/TimesheetPage';
import MDRPage from './pages/MDRPage';
import PermissionsManagementPage from './pages/PermissionsManagementPage';
import FeaturePermissionsPage from './pages/FeaturePermissionsPage';
import ClientManagementPage from './pages/ClientManagementPage';
import CommercialDashboardPage from './pages/CommercialDashboardPage';
import OpportunitiesPage from './pages/OpportunitiesPage';
import ProposalsPage from './pages/ProposalsPage';
import CapacitySnapshotPage from './pages/CapacitySnapshotPage';
import GeneralConfigPage from './pages/GeneralConfigPage';
import RevisionsConfigPage from './pages/RevisionsConfigPage';
import ClientFeedbackPage from './pages/ClientFeedbackPage';
import ProjectSelectionPage from './pages/ProjectSelectionPage';
import OAuthConsentPage from './pages/OAuthConsentPage';
import ThirdPartiesPage from './pages/ThirdPartiesPage';
import { TransmittalDownloadPage } from './pages/TransmittalDownloadPage';
import AIChatPage from './pages/AIChatPage';
import WC2026Page from './pages/WC2026Page';
import PdfEditorPage from './pages/PdfEditorPage';
import CfosPage from './pages/CfosPage';
import HelpPage from './pages/HelpPage';
import { notificationsService } from './services/notificationsService';
import { pushService } from './services/pushService';
import { localStore } from './utils/localStore';
import { useAppVersion } from './hooks/useAppVersion';
import VersionUpdateBanner from './components/VersionUpdateBanner';

const SIDEBAR_OPEN_KEY = 'sidebar_open_v1';

const App: React.FC = () => {
  const { user, logout, loading: authLoading, sessionLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  // Sidebar state
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Match sidebar breakpoint
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const isMDRPage = location.pathname === '/mdr';
  const { updateAvailable, versionInfo, dismiss, refresh } = useAppVersion();

  // Nautilus Console Design System tokens
  const isDark = theme.palette.mode === 'dark';
  const NAUTILUS = { deepOcean: '#0f2134', sand: '#ccba9b', abyssal: '#0a1622' };
  const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
  const surfaceBg = isDark ? NAUTILUS.deepOcean : '#ffffff';

  useEffect(() => {
    let cancelled = false;

    void localStore.get(SIDEBAR_OPEN_KEY).then((saved) => {
      if (cancelled) {
        return;
      }
      if (typeof saved === 'boolean') {
        setSidebarOpen(saved);
      } else {
        setSidebarOpen(!isMobile);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [isMobile]);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggleSidebar = (isOpen: boolean) => {
    setSidebarOpen(isOpen);
    void localStore.set(SIDEBAR_OPEN_KEY, isOpen);
  };

  // Skip link for accessibility
  const handleSkipToMain = () => {
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.focus();
      mainContent.scrollIntoView();
    }
  };

  // Fetch unread notifications count
  const fetchUnreadCount = async () => {
    if (user) {
      try {
        const response = await notificationsService.getUnreadCount();
        setUnreadCount(response.unreadCount);
      } catch (error) {
        console.error('Failed to fetch unread count:', error);
      }
    }
  };

  useEffect(() => {
    fetchUnreadCount();
  }, [user]);

  // Refresh unread count periodically
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(fetchUnreadCount, 30000); // Every 30 seconds
    return () => clearInterval(interval);
  }, [user]);

  // Register service worker and subscribe to push notifications
  useEffect(() => {
    if (user && pushService.isSupported()) {
      const initializePushNotifications = async () => {
        try {
          // Register service worker
          const registration = await pushService.registerServiceWorker();

          if (registration) {
            // Check if already subscribed
            const existingSubscription = await registration.pushManager.getSubscription();

            if (!existingSubscription) {
              // Not subscribed - prompt user
              console.log('No existing subscription, prompting user...');
              await pushService.subscribeToNotifications();
            } else {
              console.log('Already subscribed to push notifications');
            }
          }
        } catch (error) {
          console.error('Failed to initialize push notifications:', error);
        }
      };

      initializePushNotifications();
    }
  }, [user]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    const handleServiceWorkerMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; url?: string; notificationId?: number } | null;
      if (data?.type === 'NOTIFICATION_NAVIGATE' && data.url) {
        navigate(data.url);
        if (data.notificationId && user) {
          notificationsService.markAsRead(data.notificationId)
            .then(() => fetchUnreadCount())
            .catch((error) => console.error('Failed to mark push notification as read:', error));
        }
      }
    };

    navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
    };
  }, [navigate, user]);

  // Show loading screen while checking authentication
  if (authLoading) {
    return (
      <LoadingPage />
    );
  }
  if (sessionLoading) {
    return (
      <SessionLoadingScreen />
    );
  }

  // Import scaled drawer width from theme for consistent sizing
  const drawerWidth = scaledDrawerWidth;

  return (
    <UploadProvider>
      <PdfViewerProvider>
      <CopyProvider>
        <Box sx={{ flexGrow: 1, minHeight: '100vh', backgroundColor: 'background.default' }}>
          {updateAvailable && versionInfo && (
            <VersionUpdateBanner
              versionInfo={versionInfo}
              onRefresh={refresh}
              onDismiss={dismiss}
            />
          )}
          {/* Skip link for accessibility */}
          <a
            href="#main-content"
            className="skip-link"
            style={{
              position: 'absolute',
              top: '-40px',
              left: '6px',
              background: '#000',
              color: '#fff',
              padding: '8px',
              textDecoration: 'none',
              borderRadius: '4px',
              zIndex: 1000,
              fontSize: '14px',
              transition: 'top 0.3s',
            }}
            onFocus={(e) => {
              e.currentTarget.style.top = '6px';
            }}
            onBlur={(e) => {
              e.currentTarget.style.top = '-40px';
            }}
          >
            Skip to main content
          </a>

          <Routes>
            {/* Public routes */}

            <Route path="/login" element={<LoginPage />} />
            <Route path="/transmittals/:token" element={<TransmittalDownloadPage />} />
            <Route
              path="/oauth/consent"
              element={
                <ProtectedRoute>
                  <OAuthConsentPage />
                </ProtectedRoute>
              }
            />

            {/* Protected routes */}
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <>
                    {/* App Bar */}
                    <HamburgerMenu
                      isOpen={sidebarOpen}
                      setIsOpen={handleToggleSidebar}
                    />
                    {/* App Bar */}
                    <AppBar
                      position="fixed"
                      elevation={0}
                      sx={(theme) => ({
                        zIndex: theme.zIndex.drawer + 1,
                        bgcolor: isDark ? '#ffffff' : NAUTILUS.deepOcean,
                        borderBottom: `1px solid ${panelBorder}`,
                        transition: theme.transitions.create(['width', 'margin'], {
                          easing: theme.transitions.easing.sharp,
                          duration: theme.transitions.duration.leavingScreen,
                        }),
                      })}
                    >
                      <Toolbar>
                        {user && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Hamburger
                              toggled={sidebarOpen}
                              toggle={handleToggleSidebar}
                              size={24}
                              color={theme.palette.primary.contrastText}
                              duration={0.3}
                              easing="ease-in-out"
                            />
                            <Logo size="medium" clickable={true} showText={false} />
                          </Box>
                        )}
                        <Box sx={{ flexGrow: 1 }} />

                        {user && (
                          <>
                            <NotificationsCenter
                              unreadCount={unreadCount}
                              onUnreadCountChange={setUnreadCount}
                            />
                            <IconButton
                              color="inherit"
                              onClick={handleMenu}
                              aria-label="User menu"
                              sx={{
                                mr: 1,
                                color: theme => theme.palette.mode === 'dark' ? '#0f2134' : 'inherit'
                              }}
                            >
                              <UserAvatar user={user} sx={{ width: 32, height: 32 }} />
                            </IconButton>
                            <Menu
                              id="menu-appbar"
                              anchorEl={anchorEl}
                              anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                              }}
                              keepMounted
                              transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                              }}
                              open={Boolean(anchorEl)}
                              onClose={handleClose}
                              PaperProps={{
                                sx: {
                                  mt: 1.5,
                                  borderRadius: 1,
                                  bgcolor: surfaceBg,
                                  border: `1px solid ${panelBorder}`,
                                  boxShadow: 'none',
                                  '& .MuiMenuItem-root': {
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    '&:hover': { bgcolor: isDark ? alpha(NAUTILUS.sand, 0.08) : 'action.hover' }
                                  }
                                }
                              }}
                              sx={getZoomCompensatedSx()}
                            >
                              <MenuItem disabled>
                                <Typography variant="body1">{user.name}</Typography>
                              </MenuItem>
                              <MenuItem onClick={() => { navigate('/profile'); handleClose(); }}>
                                Details
                              </MenuItem>
                              <MenuItem onClick={() => { logout(); handleClose(); }}>
                                Logout
                              </MenuItem>
                            </Menu>
                          </>
                        )}
                      </Toolbar>
                    </AppBar>

                    {/* Main Content */}
                    <Container
                      maxWidth={false}
                      sx={(theme) => ({
                        pt: 10,
                        pb: 4,
                        px: isMDRPage ? 2 : { xs: 2, sm: 3, md: 4 },
                        minHeight: '100vh',
                        transition: theme.transitions.create(['margin', 'width'], {
                          easing: theme.transitions.easing.sharp,
                          duration: theme.transitions.duration.leavingScreen,
                        }),
                        marginLeft: {
                          md: sidebarOpen ? `${drawerWidth}px` : 0
                        },
                        width: {
                          xs: '100%',
                          md: sidebarOpen ? `calc(100% - ${drawerWidth}px)` : '100%'
                        }
                      })}
                      id="main-content"
                      tabIndex={-1}
                      component="main"
                      role="main"
                      aria-label="Main content"
                    >
                      <Routes>                        <Route path="/" element={<DashboardPage />} />
                        <Route path="/profile" element={<UserProfilePage />} />
                        <Route path="/ai-chat" element={
                          <ProtectedRoute requiredFeature={[CAP.AI_ASSISTANT]}>
                            <AIChatPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/pdf-editor" element={
                          <ProtectedRoute requiredFeature={[CAP.PDF_EDITOR]}>
                            <PdfEditorPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/cfos" element={
                          <ProtectedRoute requiredFeature={[CAP.CFOS]}>
                            <CfosPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/help" element={
                          <ProtectedRoute requiredFeature={[CAP.HELP]}>
                            <HelpPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/wc2026" element={
                          <ProtectedRoute requiredFeature={[CAP.WC2026]}>
                            <WC2026Page />
                          </ProtectedRoute>
                        } />
                        <Route path="/projects" element={<ProjectsPage />} />
                        <Route path="/projects/create" element={
                          <ProtectedRoute requiredFeature={[CAP.CREATE_PROJECT]}>
                            <ProjectCreationPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/projects/edit" element={<ProjectSelectionPage />} />
                        <Route path="/projects/:projectId/edit" element={
                          <ProjectAccessGate>
                            <ProjectCreationPage />
                          </ProjectAccessGate>
                        } />
                        <Route path="/projects/:projectId/revisions" element={
                          <ProjectAccessGate>
                            <ProjectRevisionsPage />
                          </ProjectAccessGate>
                        } />
                        <Route path="/projects/:projectId/documents" element={
                          <ProjectAccessGate>
                            <DocumentsPage />
                          </ProjectAccessGate>
                        } />
                        <Route path="/projects/:projectId/documents/:documentId/revisions" element={
                          <ProjectAccessGate>
                            <DocumentRevisionsPage />
                          </ProjectAccessGate>
                        } />
                        <Route path="/sandboxes/:projectId/:revisionId" element={
                          <ProjectAccessGate>
                            <SandboxesPage />
                          </ProjectAccessGate>
                        } />
                        <Route path="/lcms/:projectId/:revisionId/:sandboxId" element={
                          <ProjectAccessGate>
                            <LCMsPage />
                          </ProjectAccessGate>
                        } />
                        <Route path="/lcm-lab" element={<LCMLabPage />} />
                        <Route path="/lcm-lab/:projectId/:revisionId/:sandboxId" element={
                          <ProjectAccessGate>
                            <LCMLabPage />
                          </ProjectAccessGate>
                        } />
                        <Route path="/lcm-lab/:projectId/:revisionId/:sandboxId/:lcmId" element={
                          <ProjectAccessGate>
                            <LCMLabPage />
                          </ProjectAccessGate>
                        } />
                        <Route path="/results" element={<ResultsPage />} />
                        <Route path="/time-off" element={<TimeOffPage />} />
                        <Route path="/timesheet" element={<TimesheetPage />} />
                        <Route path="/time-management" element={<Navigate to="/time-off" replace />} />
                        <Route path="/mdr" element={<MDRPage />} />
                        <Route path="/permissions" element={
                          <ProtectedRoute requiredFeature={[CAP.MANAGE_WP_PERMISSIONS]}>
                            <PermissionsManagementPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/clients" element={
                          <ProtectedRoute requiredFeature={[CAP.MANAGE_CLIENTS]}>
                            <ClientManagementPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/sales/dashboard" element={
                          <ProtectedRoute requiredFeature={[CAP.SALES_TOOLS]}>
                            <CommercialDashboardPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/sales/leads" element={
                          <ProtectedRoute requiredFeature={[CAP.SALES_TOOLS]}>
                            <OpportunitiesPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/sales/opportunities" element={<Navigate to="/sales/leads" replace />} />
                        <Route path="/sales/proposals" element={
                          <ProtectedRoute requiredFeature={[CAP.SALES_TOOLS]}>
                            <ProposalsPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/sales/capacity" element={
                          <ProtectedRoute requiredFeature={[CAP.SALES_TOOLS]}>
                            <CapacitySnapshotPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/hr/management" element={
                          <ProtectedRoute requiredFeature={[CAP.HR_ADMIN_ACTIONS]}>
                            <HRManagementPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/hr/user/:userId" element={
                          <ProtectedRoute requiredFeature={[CAP.HR_ADMIN_ACTIONS]}>
                            <HRUserDetailsPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/hr/cv-bank" element={
                          <ProtectedRoute requiredFeature={[CAP.MANAGE_CV_BANK]}>
                            <CVBankPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/hr/expenses" element={
                          <ProtectedRoute requiredFeature={[CAP.EXPENSES, CAP.HR_ADMIN_ACTIONS]}>
                            <ExpensesPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/hr/documents" element={
                          <ProtectedRoute requiredFeature={[CAP.MY_DOCUMENTS]}>
                            <EmployeeDocumentsPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/hr" element={
                          <ProtectedRoute requiredFeature={[CAP.HR]}>
                            <HRPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/users" element={
                          <ProtectedRoute requiredFeature={[CAP.USER_MANAGEMENT]}>
                            <UsersPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/feature-permissions" element={
                          <ProtectedRoute requiredRole={[ROLE.ADMIN]}>
                            <FeaturePermissionsPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/general-config" element={
                          <ProtectedRoute requiredFeature={[CAP.GENERAL_CONFIG]}>
                            <GeneralConfigPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/third-parties" element={
                          <ProtectedRoute requiredFeature={[CAP.MANAGE_THIRD_PARTIES]}>
                            <ThirdPartiesPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/pmo/client-feedback" element={
                          <ProtectedRoute requiredFeature={[CAP.CLIENT_FEEDBACK]}>
                            <ClientFeedbackPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/pmo/revisions-config" element={
                          <ProtectedRoute requiredFeature={[CAP.DCC_CONFIG]}>
                            <RevisionsConfigPage />
                          </ProtectedRoute>
                        } />
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>
                    </Container>
                  </>
                </ProtectedRoute>
              }
            />
          </Routes>
        </Box>
      </CopyProvider>
      </PdfViewerProvider>
    </UploadProvider >
  );
};

export default App;